import { useMemo } from 'react'

import {
  digObject, formatDate, matchFilterArrayIncludes,
  matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { pendingStatusKeys } from '@functions/status'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { OrderModel, OrderRequestOptions, StatusModel } from '@models/types'
import type { ModuleState } from '@redux/modules/types'
import type { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['artworks', 'orderItems', 'orders', 'statuses']

type MatchCompatiblePublicationParams = {
  filterCompatiblePublicationId?: number,
  orderItemIds?: number[],
  orderItems: EntitiesState['orderItems'],
  serviceProducts: EntitiesState['serviceProducts'],
}

const matchCompatiblePublication = (params: MatchCompatiblePublicationParams) => {
  const {
    filterCompatiblePublicationId, orderItemIds, orderItems, serviceProducts,
  } = params

  if (!filterCompatiblePublicationId || !orderItemIds?.length) return true

  const match = orderItemIds.some((orderItemId) => {
    const orderItem = orderItems[orderItemId] || {}
    const serviceProduct = orderItem.service_product_id && serviceProducts[orderItem.service_product_id]
      ? serviceProducts[orderItem.service_product_id]
      : {}

    // @ts-expect-error TS2339
    const compatiblePublicationIds = serviceProduct?.options?.publication?.compatible_publications || []

    return compatiblePublicationIds.includes(filterCompatiblePublicationId)
  })

  return match
}

type MatchStatusGroupKeyParams = {
  artworkStatus: StatusModel,
  filterStatusGroupKey?: 'agency_orders_not_pending' | 'artwork_approved' | 'artwork_pending' | 'pending' | 'complete',
  ownerType: string,
  status: StatusModel,
}

const matchStatusGroupKey = (params: MatchStatusGroupKeyParams) => {
  const {
    filterStatusGroupKey, status, ownerType, artworkStatus,
  } = params

  if (!filterStatusGroupKey) return true

  if (filterStatusGroupKey === 'agency_orders_not_pending'){
    return status.key !== 'pending' && (ownerType === 'Agency' || !ownerType)
  }

  if (filterStatusGroupKey === 'artwork_approved'){
    return artworkStatus?.key === 'approved'
  }

  if (filterStatusGroupKey === 'artwork_pending'){
    return artworkStatus?.key === 'pending' || !artworkStatus?.id
  }

  return filterStatusGroupKey === 'pending'
    // @ts-expect-error TS2345
    ? pendingStatusKeys.includes(status.key)
    // @ts-expect-error TS2345
    : !pendingStatusKeys.includes(status.key)
}

export type OrderFilters = {
  agency_id?: number,
  approval_date_start?: string,
  approval_date_end?: string,
  brand_id?: number,
  campaign_agency_id?: number,
  campaign_id?: number,
  campaign_marketing_type?: string,
  compatible_publication_id?: number,
  exclude_hidden_campaigns?: boolean,
  includeNullApprovals?: boolean,
  limit?: number,
  order_by?: string,
  order_type?: string,
  owner_type?: string,
  priority_orders_by_user_id?: number[],
  provider_id?: number,
  publication_date_start?: string,
  publication_edition_id?: number,
  publication_id?: number,
  status_group_key?: 'agency_orders_not_pending' | 'complete'| 'pending',
  status_id?: number,
  string?: string,
}

type UseOrdersOptions = {
  filters?: OrderFilters,
  performHttpRequests?: boolean,
  requestOptions?: OrderRequestOptions,
  sortBy?: string,
}

function useOrders(options: UseOrdersOptions = {}) {
  const { filters = {}, requestOptions, sortBy } = options
  const {
    agency_id: filterAgencyId,
    approval_date_start: filterApprovalDateStart,
    approval_date_end: filterApprovalDateEnd,
    brand_id: filterBrandId,
    campaign_agency_id: filterCampaignAgencyId,
    campaign_id: filterCampaignId,
    campaign_marketing_type: filterMarketingType,
    compatible_publication_id: filterCompatiblePublicationId,
    exclude_hidden_campaigns: filterHiddenCampaigns,
    includeNullApprovals: filterIncludeNullApprovals,
    limit: filterLimit,
    order_by: orderBy = 'id desc',
    order_type: filterOrderType,
    priority_orders_by_user_id: filterPriorityOrdersByUserId,
    provider_id: filterProviderId,
    publication_date_start: filterPublicationDateStart,
    publication_edition_id: filterPublicationEditionId,
    publication_id: filterPublicationId,
    status_group_key: filterStatusGroupKey,
    status_id: filterStatusId,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      artworks: artworksUpdatedAt,
      orderItems: orderItemsUpdatedAt,
      orders: ordersUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies,
    artworks,
    campaigns,
    orderItems,
    orders,
    publicationEditions,
    serviceProducts,
    statuses,
  } = entities

  const filteredOrders = useMemo(() => {
    const filtered = Object.values(orders).filter((order: OrderModel) => {
      const {
        agency_id,
        campaign_id,
        dates,
        id,
        items,
        order_type,
        owner_type,
        priority_campaign_user_ids = [],
        provider_id,
        publication_edition_id,
        publication_id,
        status_id,
      } = order

      const orderItemsArtworkStatus = items?.map((item: number) => {
        const orderItem = orderItems[item]
        const { artwork_id } = orderItem || {}

        const artwork = artwork_id && artworks[artwork_id] ? artworks[artwork_id] : {}
        const artworkStatus = artwork && statuses[orderItem?.status_id] ? statuses[orderItem.status_id] : {}

        return artworkStatus
      })

      const agency = agencies[agency_id] || {}
      const campaign = campaign_id ? campaigns[campaign_id] || {} : {}
      const publicationEdition = publication_edition_id ? publicationEditions[publication_edition_id] || {} : {}
      const status = status_id && statuses[status_id] ? statuses[status_id] : {}

      // @ts-expect-error TS2339
      const publicationDate = formatDate(publicationEdition?.dates?.publication?.date, 'dd/MM/yyyy')

      const campaignHidden = digObject(campaign, 'hidden', false)
      const matchCampaignHidden = filterHiddenCampaigns ? campaignHidden.toString() !== filterHiddenCampaigns : true

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchBrand = matchFilterNumber(agency.brand_id, filterBrandId)
      const matchCampaign = matchFilterNumber(campaign_id, filterCampaignId)
      const matchProvider = matchFilterNumber(provider_id, filterProviderId)

      const isAgencyMarketing = filterMarketingType === 'agency_marketing'
      const matchCampaignMarketingType = filterMarketingType
        // @ts-expect-error TS2339
        ? campaign?.agency_marketing === isAgencyMarketing
        : true

      const matchOrderType = matchFilterString(order_type, filterOrderType)

      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationEdition = matchFilterNumber(publication_edition_id, filterPublicationEditionId)
      const matchPublicationStartDate = matchFilterBetweenDates(publicationDate, filterPublicationDateStart, null)
      // TODO Check why unused
      const matchCompatiblePublicationId = matchCompatiblePublication({
        filterCompatiblePublicationId,
        orderItemIds: items,
        orderItems,
        serviceProducts,
      })
      const matchStatus = matchFilterNumber(status_id, filterStatusId)
      const matchesStatusGroupKey = matchStatusGroupKey({
        // @ts-expect-error TS2739
        filterStatusGroupKey, status, ownerType: owner_type, artworkStatus: orderItemsArtworkStatus,
      })

      const matchId = id.toString() === filterString
      // @ts-expect-error TS2339
      const matchTitle = matchFilterString(campaign?.title, filterString)
      const matchString = matchId || matchTitle

      const approvalDateString = digObject(dates, 'approval.date_time')
      const approvalDateFormatted = formatDate(approvalDateString, 'dd/MM/yyyy HH:mm')
      const noApprovalDate = approvalDateString === '' // returns orders that are approval null

      // @ts-expect-error TS2339
      const matchCampaignAgency = matchFilterNumber(campaign?.agency_id, filterCampaignAgencyId)

      const matchDates = (filterApprovalDateStart || filterApprovalDateEnd)
        ? matchFilterBetweenDates(approvalDateFormatted, filterApprovalDateStart, filterApprovalDateEnd)
      || (filterIncludeNullApprovals && noApprovalDate) : true

      const matchPriorityCampaigns = filterPriorityOrdersByUserId
        ? matchFilterArrayIncludes(priority_campaign_user_ids, Number(filterPriorityOrdersByUserId)) : true

      return matchAgency
        && matchBrand
        && matchCampaign
        && matchCampaignAgency
        && matchCampaignHidden
        && matchCampaignMarketingType
        && matchDates
        && matchOrderType
        && matchPriorityCampaigns
        && matchProvider
        && matchPublication
        && matchPublicationEdition
        && matchPublicationStartDate
        && matchStatus
        && matchString
        && matchesStatusGroupKey
    })

    const sort = sortBy || orderBy
    const [column, direction] = sort.split(' ')

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, direction, column)
  }, [
    artworksUpdatedAt,
    orderItemsUpdatedAt,
    ordersUpdatedAt,
    statusesUpdatedAt,
    JSON.stringify(filters),
  ])

  const filteredOrdersCount = filteredOrders.length
  const hasFilteredOrders = !!filteredOrdersCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredOrdersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrders } = useReduxAction(
    'orders',
    'loadOrders',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrders,
    filteredOrdersCount,
    hasFilteredOrders,
    loading: loadingOrders,
  }
}

export default useOrders
